"use client";

import { colors } from "@jesus-central-church/shared";
import { motion, Variants } from "motion/react";
import { cn } from "../../lib/utils";
import { Badge } from "../badge";
import { Heading } from "../heading";
import { Tooltip } from "../tooltip";

export interface StatementScripture {
  scripture: string;
  reference: string;
}

export interface StatementProperties {
  subtitle: string;
  statement: string;
  scriptures?: StatementScripture[];
  className?: string;
  textColor?: string;
}

const fadeInVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

export function Statement({
  subtitle,
  statement,
  scriptures = [],
  className,
  textColor = colors.FREEDOM,
}: StatementProperties) {
  return (
    <motion.div
      variants={fadeInVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className={cn("flex flex-col py-10 relative group/feature", className)}
    >
      {/* Subtle hover overlay that adapts to the text color */}
      <div
        className="opacity-0 group-hover/feature:opacity-100 transition duration-300 absolute inset-0 h-full w-full pointer-events-none rounded-lg"
        style={{
          background: `linear-gradient(to top, color-mix(in srgb, var(--${textColor}) 8%, transparent), transparent)`,
        }}
      />
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div
          className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full transition-all duration-200 origin-center"
          style={{
            backgroundColor: `color-mix(in srgb, var(--${textColor}) 30%, transparent)`,
          }}
        />
        <Heading
          variant="h3"
          className="group-hover/feature:translate-x-2 transition duration-200 inline-block"
          style={{
            color: `var(--${textColor})`,
          }}
        >
          {subtitle}
        </Heading>
      </div>
      <p
        className="text-sm max-w-xs relative z-10 px-10 mb-4 opacity-80"
        style={{
          color: `var(--${textColor})`,
        }}
      >
        {statement}
      </p>
      {scriptures.length > 0 && (
        <div className="relative z-10 px-10 flex flex-wrap gap-2">
          {scriptures.map((scripture, index) => (
            <Tooltip
              key={index}
              content={
                <>
                  <p className="font-semibold mb-2 text-sm">
                    {scripture.reference}
                  </p>
                  <p className="text-xs leading-relaxed italic">
                    {scripture.scripture}
                  </p>
                </>
              }
            >
              <Badge
                backgroundColor={textColor}
                textColor={
                  textColor === colors.FREEDOM
                    ? colors.BOLD_DARK
                    : colors.FREEDOM
                }
                text={scripture.reference}
              />
            </Tooltip>
          ))}
        </div>
      )}
    </motion.div>
  );
}
